import { useEffect, useState } from "react"

// ** Third Party Components
import { Filter, X } from "lucide-react"
import { useNavigate, useSearchParams } from "react-router-dom"

// ** Types
import { DictType } from "@pma/types"

// ** Theme Components
import { Button } from "@components/common/button"
import { Popover, PopoverContent, PopoverTrigger } from "@components/common/popover"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../common/select"

// ** Custom Hooks
import { useAuth } from "@pma/stores/auth-store"
import useTranslator from "@pma/hooks/use-translator"
import { Switch } from "../common/switch"
import { cn } from "@pma/utils/helpers"

interface FilterProjectsTableProps<TData> {
  projectStatuses: DictType[]
  activeStatus: string
  activeOwner: string
}

export function FilterProjectsTable<TData>({
  projectStatuses,
  activeStatus,
  activeOwner,
}: FilterProjectsTableProps<TData>) {
  const navigate = useNavigate()
  const { authConfig } = useAuth()
  const [searchParam] = useSearchParams()
  const { translate, locale } = useTranslator()
  const isHebrew = locale == "he"
  const organizationUsers = authConfig?.organizationUsers || []
  const value = searchParam.get("showDeleted")

  const [open, setOpen] = useState(false)
  const [showDeleted, setShowDeleted] = useState<boolean>(false)
  const [selectedOwner, setSelectedOwner] = useState<any>(searchParam.get("ownerId") || "")
  const [selectedStatus, setSelectedStatus] = useState<any>(searchParam.get("status") || "")

  const selectStatus = () => {
    const newParams = new URLSearchParams(searchParam)
    if (selectedStatus !== "") newParams.set("status", selectedStatus)
    else newParams.delete("status")

    navigate(`?${newParams.toString()}`)

    return
  }

  const selectOwner = () => {
    const newParams = new URLSearchParams(searchParam)
    if (selectedOwner !== "") newParams.set("ownerId", selectedOwner)
    else newParams.delete("ownerId")

    navigate(`?${newParams.toString()}`)

    return
  }

  useEffect(() => {
    setSelectedStatus(String(activeStatus))
  }, [activeStatus])

  useEffect(() => {
    setSelectedOwner(String(activeOwner))
  }, [activeOwner])

  useEffect(() => {
    selectStatus()
  }, [selectedStatus])

  useEffect(() => {
    selectOwner()
  }, [selectedOwner])

  useEffect(() => {
    const newParams = new URLSearchParams(searchParam)
    if (showDeleted) newParams.set("showDeleted", "true")
    else newParams.delete("showDeleted")

    navigate(`?${newParams.toString()}`)
  }, [showDeleted])

  useEffect(() => {
    if (value === "true") setShowDeleted(true)
    else setShowDeleted(false)
  }, [value])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant='outline' className='rounded-full' size={"icon"}>
          <Filter size={20} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-80' align={isHebrew ? "start" : "end"}>
        <div className='grid gap-4'>
          <div className='space-y-2'>
            <h4 className={cn("text-sm font-semibold", isHebrew ? "text-right" : "text-left")}>
              {translate("Status")}
            </h4>
            <div className='flex flex-wrap items-center gap-2'>
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <div className='relative w-full'>
                  <SelectTrigger
                    className={cn(
                      "w-full rounded-lg sm:ml-auto",
                      selectedStatus != "" ? "pr-10" : "",
                      isHebrew ? "rtl" : "ltr",
                    )}
                  >
                    <SelectValue placeholder={translate("Select Status")} />
                  </SelectTrigger>
                  {selectedStatus && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation() // Prevent opening the dropdown
                        setSelectedStatus("") // Clear selection
                      }}
                      className='absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600'
                      aria-label='Clear selection'
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
                <SelectContent className={cn("rounded-xl", isHebrew ? "rtl" : "ltr")}>
                  {projectStatuses?.map((status, index) => (
                    <SelectItem value={`${status?.id}`} className='rounded-lg' key={index}>
                      {status?.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className='space-y-2'>
            <h4 className={cn("text-sm font-semibold", isHebrew ? "text-right" : "text-left")}>
              {translate("Owner")}
            </h4>
            <div className='flex flex-wrap items-center gap-2'>
              <Select value={selectedOwner} onValueChange={setSelectedOwner}>
                <div className='relative w-full'>
                  <SelectTrigger
                    className={cn(
                      "w-full rounded-lg sm:ml-auto",
                      selectedOwner != "" ? "pr-10" : "",
                      isHebrew ? "rtl" : "ltr",
                    )}
                  >
                    <SelectValue placeholder={translate("Select Owner")} />
                  </SelectTrigger>
                  {selectedOwner && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation() // Prevent opening the dropdown
                        setSelectedOwner("") // Clear selection
                      }}
                      className='absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer text-gray-400 hover:text-gray-600'
                      aria-label='Clear selection'
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
                <SelectContent className={cn("rounded-xl", isHebrew ? "rtl" : "ltr")}>
                  {organizationUsers?.map((user: any, index: number) => (
                    <SelectItem value={`${user?.id}`} className='rounded-lg' key={index}>
                      {user?.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className={cn("space-y-2", isHebrew ? "rtl" : "ltr")}>
            <h4 className='text-sm font-semibold'>{translate("Show Deleted Projects")}</h4>
            <Switch
              className='h-6 w-11'
              checked={showDeleted}
              onCheckedChange={(checked) => setShowDeleted(checked)}
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
